import React, { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { KbImage, KbCallout } from "./KbExtensions";
import KbToolbar from "./KbToolbar";
import "./KbEditor.css";

export const KB_EXTENSIONS = [
  StarterKit,
  Link.configure({ openOnClick: false, autolink: true, protocols: ["mailto"] }),
  Placeholder.configure({ placeholder: "Start writing your article…" }),
  Table.configure({ resizable: true }), TableRow, TableHeader, TableCell,
  KbImage, KbCallout,
];
const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

export default function KbEditor({ value, onChange, disabled = false }) {
  const [imageError, setImageError] = useState("");
  const fileInput = useRef(null);
  const editor = useEditor({
    extensions: KB_EXTENSIONS,
    content: value || EMPTY_DOC,
    editable: !disabled,
    onUpdate: ({ editor: currentEditor }) => onChange?.(currentEditor.getJSON()),
  });

  useEffect(() => {
    if (!editor) return;
    const incoming = value || EMPTY_DOC;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(incoming)) editor.commands.setContent(incoming, false);
  }, [editor, value]);
  useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);

  const insertImage = async (file) => {
    if (!editor || !file) return;
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
      setImageError("Choose a PNG, JPEG, GIF or WebP image (SVG is not supported)."); return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError("Image exceeds 2 MB. Resize it before inserting."); return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Unable to read image"));
        reader.readAsDataURL(file);
      });
      editor.chain().focus().setImage({ src: dataUrl, alt: file.name || "Pasted screenshot" }).run();
      setImageError("");
    } catch { setImageError("Unable to insert image."); }
  };

  const onPaste = (event) => {
    if (disabled) return;
    const files = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile()).filter(Boolean);
    if (!files.length) return;
    event.preventDefault();
    files.forEach(insertImage);
  };

  if (!editor) return <div className="kbv2-editor-loading">Loading editor…</div>;
  return (
    <div className="kbv2-editor-shell" onPasteCapture={onPaste}>
      <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden
        onChange={(event) => { insertImage(event.target.files?.[0]); event.target.value = ""; }} />
      <KbToolbar editor={editor} disabled={disabled} onUploadImage={() => fileInput.current?.click()} />
      {imageError && <p className="kbv2-error" role="alert">{imageError}</p>}
      <EditorContent editor={editor} className="kbv2-content" />
    </div>
  );
}
